import earcut, { flatten } from "earcut";
import { lngLatElevToMercator } from "./webgl-maplibre.obstacle-3d-layer";

/**
 * Turns procedure GeoJSON features into flat 3D geometry for the procedure
 * layer: line segments (with per-segment style and along-line distance) plus
 * filled triangles, all at the feature's `airportElevationM`. Pure — no map
 * instance, no side effects. Mirrors the 2D MapLibre styles `vm-line`,
 * `vm-line-dashed` and `vm-polygon-fill` (webgl-maplibre.layers.ts).
 */
export interface ProcedureGeometry {
  /** 6 numbers per segment: A x,y,z then B x,y,z, mercator units. */
  lines: Float64Array;
  /** 7 numbers per segment: r, g, b, a (0..1, not premultiplied), halfWidthPx, dashOn, dashOff. */
  lineStyle: Float32Array;
  /** 2 numbers per segment: metres along its line part/ring from the part start, at A and at B. */
  lineDistance: Float64Array;
  /** Triangle list, 7 numbers per vertex: mercator x,y,z then r,g,b,a (0..1, not premultiplied). */
  fills: Float64Array;
}

const EARTH_RADIUS_M = 6371000;

function haversineMetres(a: number[], b: number[]): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h = sinDLat * sinDLat + Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * sinDLng * sinDLng;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Accepts 'rgba(r, g, b, a)', 'rgb(r,g,b)', '#rrggbb', '#rgb'. Falls back to black, alpha 1. */
function parseColor(input: unknown): [number, number, number, number] {
  if (typeof input !== "string") return [0, 0, 0, 1];
  const str = input.trim();
  const m = str.match(/^rgba?\(\s*([^)]+)\)$/i);
  if (m) {
    const parts = m[1].split(",").map((x) => parseFloat(x.trim()));
    if (parts.length >= 3 && parts.slice(0, 3).every((n) => Number.isFinite(n))) {
      const a = parts.length >= 4 && Number.isFinite(parts[3]) ? parts[3] : 1;
      return [
        Math.min(1, Math.max(0, parts[0] / 255)),
        Math.min(1, Math.max(0, parts[1] / 255)),
        Math.min(1, Math.max(0, parts[2] / 255)),
        Math.min(1, Math.max(0, a)),
      ];
    }
  }
  if (str[0] === "#") {
    let h = str.slice(1);
    if (h.length === 3)
      h = h
        .split("")
        .map((c) => c + c)
        .join("");
    if (h.length === 6) {
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      if ([r, g, b].every((n) => Number.isFinite(n))) return [r / 255, g / 255, b / 255, 1];
    }
  }
  return [0, 0, 0, 1];
}

function dashLengths(value: unknown): [number, number] {
  if (Array.isArray(value) && value.length === 2 && Number.isFinite(value[0]) && Number.isFinite(value[1])) {
    return [value[0] as number, value[1] as number];
  }
  return [0, 0];
}

/** Appends the closing vertex when the ring does not already repeat its first vertex. */
function closedRingPath(ring: number[][]): number[][] {
  if (ring.length < 2) return ring;
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) return ring;
  return [...ring, first];
}

export function procedureGeometry(features: GeoJSON.Feature[], zoom: number): ProcedureGeometry {
  const lines: number[] = [];
  const lineStyle: number[] = [];
  const lineDistance: number[] = [];
  const fills: number[] = [];

  for (const feature of features) {
    const properties: Record<string, unknown> = feature.properties ?? {};
    const elevation = properties["airportElevationM"];
    if (typeof elevation !== "number" || !Number.isFinite(elevation)) continue;
    const geometry = feature.geometry;
    if (!geometry) continue;

    const strokeWidthRaw = properties["strokeWidth"];
    const strokeWidth = typeof strokeWidthRaw === "number" ? strokeWidthRaw : 1;
    const halfWidthPx = strokeWidth / 2;
    const hasDash = properties["lineDash"] !== undefined && properties["lineDash"] !== null;
    const strokeColorInput = properties["strokeColor"] ?? (hasDash ? properties["dashColor"] : undefined);
    const [r, g, b, a] = parseColor(strokeColorInput);
    const [dashOn, dashOff] = dashLengths(properties["lineDash"]);

    const addPath = (coords: number[][]): void => {
      if (!(strokeWidth > 0)) return;
      let dist = 0;
      for (let i = 1; i < coords.length; i++) {
        const p = coords[i - 1];
        const q = coords[i];
        const A = lngLatElevToMercator(p[0], p[1], elevation);
        const B = lngLatElevToMercator(q[0], q[1], elevation);
        lines.push(...A, ...B);
        lineStyle.push(r, g, b, a, halfWidthPx, dashOn, dashOff);
        const length = haversineMetres(p, q);
        lineDistance.push(dist, dist + length);
        dist += length;
      }
    };

    const addFill = (rings: number[][][]): void => {
      const fillColorInput = properties["fillColor"];
      if (fillColorInput === undefined || fillColorInput === null) return;
      const fillColor = parseColor(fillColorInput);
      if (fillColor[3] <= 0) return;
      if (zoom >= 15 && properties["heliportSector"] === true) return;
      const flat = flatten(rings);
      const indices = earcut(flat.vertices, flat.holes, flat.dimensions);
      const coords = rings.flat();
      for (const index of indices) {
        const p = coords[index];
        const v = lngLatElevToMercator(p[0], p[1], elevation);
        fills.push(v[0], v[1], v[2], fillColor[0], fillColor[1], fillColor[2], fillColor[3]);
      }
    };

    switch (geometry.type) {
      case "LineString":
        addPath(geometry.coordinates as number[][]);
        break;
      case "MultiLineString":
        for (const part of geometry.coordinates as number[][][]) addPath(part);
        break;
      case "Polygon": {
        const rings = geometry.coordinates as number[][][];
        for (const ring of rings) addPath(closedRingPath(ring));
        addFill(rings);
        break;
      }
      case "MultiPolygon":
        for (const polygon of geometry.coordinates as number[][][][]) {
          for (const ring of polygon) addPath(closedRingPath(ring));
          addFill(polygon);
        }
        break;
      default:
        break;
    }
  }

  return {
    lines: new Float64Array(lines),
    lineStyle: new Float32Array(lineStyle),
    lineDistance: new Float64Array(lineDistance),
    fills: new Float64Array(fills),
  };
}

/**
 * One path label per named procedure line, anchored at the middle of its longest
 * part. Mirrors the 2D MapLibre style `vm-procedure-path-labels`
 * (webgl-maplibre.layers.ts). Pure — no map instance.
 */
export interface ProcedureLabel {
  text: string;
  bold: boolean;
  color: string;
  lng: number;
  lat: number;
  /** [lng, lat] of the end vertex of the segment containing the anchor. */
  toward: [number, number];
  elevation: number;
}

/** Resolves the label text for a procedure feature. `null` means "no label". */
function procedureLabelText(properties: Record<string, unknown>): { text: string; bold: boolean } | null {
  const circuitLabel = properties["circuitAltitudeLabel"];
  if (typeof circuitLabel === "string" && circuitLabel.length > 0) return { text: circuitLabel, bold: true };
  const procedureName = properties["procedureName"];
  if (typeof procedureName === "string" && procedureName.length > 0) return { text: procedureName, bold: false };
  return null;
}

/** The line parts of a LineString/MultiLineString geometry; `null` for any other type. */
function lineStringParts(geometry: GeoJSON.Geometry): number[][][] | null {
  if (geometry.type === "LineString") return [geometry.coordinates as number[][]];
  if (geometry.type === "MultiLineString") return geometry.coordinates as number[][][];
  return null;
}

/** The part with the greatest ground length; `null` when every part has fewer than 2 vertices or zero length. */
function longestPart(parts: number[][][]): { part: number[][]; length: number } | null {
  let best: { part: number[][]; length: number } | null = null;
  for (const part of parts) {
    if (part.length < 2) continue;
    let length = 0;
    for (let i = 1; i < part.length; i++) length += haversineMetres(part[i - 1], part[i]);
    if (length <= 0) continue;
    if (!best || length > best.length) best = { part, length };
  }
  return best;
}

/** The point at half the part's ground length, and the end vertex of the segment that contains it. */
function midpointAnchor(
  part: number[][],
  length: number
): { lng: number; lat: number; toward: [number, number] } | null {
  const half = length / 2;
  let cumulative = 0;
  for (let i = 1; i < part.length; i++) {
    const a = part[i - 1];
    const b = part[i];
    const segLen = haversineMetres(a, b);
    const isLast = i === part.length - 1;
    if (half <= cumulative + segLen || isLast) {
      const fraction = segLen > 0 ? (half - cumulative) / segLen : 0;
      return {
        lng: a[0] + fraction * (b[0] - a[0]),
        lat: a[1] + fraction * (b[1] - a[1]),
        toward: [b[0], b[1]],
      };
    }
    cumulative += segLen;
  }
  return null;
}

export function procedureLabels(features: GeoJSON.Feature[]): ProcedureLabel[] {
  const labels: ProcedureLabel[] = [];

  for (const feature of features) {
    const properties: Record<string, unknown> = feature.properties ?? {};
    const elevation = properties["airportElevationM"];
    if (typeof elevation !== "number" || !Number.isFinite(elevation)) continue;
    if (!feature.geometry) continue;

    const parts = lineStringParts(feature.geometry);
    if (!parts) continue;

    const labelText = procedureLabelText(properties);
    if (!labelText) continue;

    const strokeColorInput = properties["strokeColor"];
    const color = typeof strokeColorInput === "string" && strokeColorInput.length > 0 ? strokeColorInput : "#00008b";

    const best = longestPart(parts);
    if (!best) continue;

    const anchor = midpointAnchor(best.part, best.length);
    if (!anchor) continue;

    labels.push({ ...labelText, color, lng: anchor.lng, lat: anchor.lat, toward: anchor.toward, elevation });
  }

  return labels;
}
